import { fetchWithAuth } from "./api";

// Path of the service worker file relative to the public folder root.
const SW_PATH = "/service-worker.js";

// ─── Native (Capacitor) detection ──────────────────────────────────────────
// Capacitor packages aren't imported at the top of the file — they're loaded
// dynamically only when we know we're inside a native shell. That keeps the
// web bundle from pulling in iOS/Android-only code paths.
function getCapacitor() {
  try {
    // eslint-disable-next-line global-require, import/no-unresolved
    const { Capacitor } = require("@capacitor/core");
    return Capacitor;
  } catch (_) {
    return null;
  }
}

export function isNativeApp() {
  const cap = getCapacitor();
  return !!(cap && cap.isNativePlatform && cap.isNativePlatform());
}

/** Is push supported either via web push OR via Capacitor native plugin? */
export function isPushSupported() {
  if (isNativeApp()) return true;
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Current notification permission state ('granted' | 'denied' | 'default'). */
export function getPermission() {
  if (isNativeApp()) {
    // Native permission isn't readable synchronously — we treat it as
    // 'default' until subscribeToPush() runs the async check and either
    // grants or fails. Banner just shows "Enable" until then.
    return "default";
  }
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

/**
 * Register the service worker (no-op if already registered): web push, and
 * keeping the app's files for opening with no signal (public/service-worker.js)
 * — so wherever service workers exist, push or not (iPad Safari in a tab has
 * no web push).
 */
export async function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  // navigator.serviceWorker.register is idempotent — calling it for an
  // already-registered SW returns the existing registration.
  try {
    return await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
  } catch (err) {
    console.warn("[push] SW registration failed:", err);
    return null;
  }
}

/** Convert a base64url VAPID public key into the Uint8Array PushManager needs. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * End-to-end: request permission, register SW (or native plugin), subscribe,
 * POST device to the server. Returns the registered device row on success.
 * Throws on permission denial or any failure — callers should surface a toast.
 */
export async function subscribeToPush() {
  if (isNativeApp()) return subscribeNative();
  return subscribeWeb();
}

async function subscribeWeb() {
  if (typeof Notification === "undefined") {
    throw new Error("Push not supported in this browser");
  }

  // 1. Permission
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") throw new Error("Notifications permission denied");

  // 2. SW
  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Service worker registration failed");

  // 3. Fetch the VAPID public key from the server (single source of truth)
  const keyRes = await fetchWithAuth("/v1/team-messaging/push/vapid-public-key");
  if (!keyRes.ok) {
    if (keyRes.status === 503) throw new Error("Push notifications are not yet configured on the server");
    throw new Error("Failed to fetch VAPID key");
  }
  const { publicKey } = await keyRes.json();
  if (!publicKey) throw new Error("Missing VAPID public key");

  // 4. Subscribe (reuses existing subscription if one already exists)
  const existing = await reg.pushManager.getSubscription();
  const subscription = existing || await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  // 5. Register with server
  const regRes = await fetchWithAuth("/v1/team-messaging/push/devices", {
    method: "POST",
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      platform: "web",
      appVersion: window.navigator.userAgent.slice(0, 200),
    }),
  });
  if (!regRes.ok) {
    const err = await regRes.json().catch(() => ({}));
    throw new Error(err.error || "Failed to register device");
  }
  const { device } = await regRes.json();
  return device;
}

// ─── Native (Capacitor) push listeners ─────────────────────────────────────
// State held outside subscribeNative so listeners persist across calls AND
// so initNativeListeners (called from index.js at boot) can bind the click
// handler BEFORE the user opens the messaging page. Without that, tapping a
// notification when the app is fully killed wouldn't navigate anywhere.

let nativeListenersBound = false;
let pendingTokenResolve = null;
let pendingTokenReject = null;

/** Set up Capacitor push listeners once at app boot. No permission prompt yet. */
export async function initNativeListeners() {
  if (!isNativeApp() || nativeListenersBound) return;
  let PushNotifications;
  try {
    // eslint-disable-next-line global-require, import/no-unresolved
    PushNotifications = require("@capacitor/push-notifications").PushNotifications;
  } catch (_) {
    return; // plugin not installed in this build
  }

  nativeListenersBound = true;

  await PushNotifications.addListener("registration", (t) => {
    if (pendingTokenResolve) {
      pendingTokenResolve(t.value);
      pendingTokenResolve = null;
      pendingTokenReject = null;
    }
  });

  await PushNotifications.addListener("registrationError", (err) => {
    if (pendingTokenReject) {
      pendingTokenReject(new Error(err.error || "Push registration failed"));
      pendingTokenResolve = null;
      pendingTokenReject = null;
    }
  });

  // Fires when user taps a notification from the lock screen / notification
  // center — including when the app was fully killed. Capacitor replays
  // queued events after listeners bind, so registering at boot guarantees
  // delivery.
  await PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
    const url = event.notification?.data?.url;
    if (!url) return;
    window.dispatchEvent(new CustomEvent("chariot:push-navigate", { detail: { url } }));
  });
}

async function subscribeNative() {
  // eslint-disable-next-line global-require, import/no-unresolved
  const { PushNotifications } = require("@capacitor/push-notifications");
  // eslint-disable-next-line global-require, import/no-unresolved
  const { Capacitor } = require("@capacitor/core");

  await initNativeListeners();

  // 1. Permission (iOS shows the system prompt here; Android 13+ also asks)
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") {
    throw new Error("Notifications permission denied");
  }

  // 2. Prepare to capture the token from the 'registration' listener
  const tokenPromise = new Promise((resolve, reject) => {
    pendingTokenResolve = resolve;
    pendingTokenReject = reject;
  });

  // 3. Kick registration off — fires APNs/FCM exchange
  await PushNotifications.register();

  // 4. Wait for the token (15s timeout — usually arrives within 1-2s)
  const token = await Promise.race([
    tokenPromise,
    new Promise((_, rej) => setTimeout(() => {
      if (pendingTokenReject) {
        pendingTokenReject(new Error("Push registration timed out"));
        pendingTokenResolve = null;
        pendingTokenReject = null;
      }
      rej(new Error("Push registration timed out"));
    }, 15000)),
  ]);
  if (!token) throw new Error("Empty push token");

  // 5. Register with server. Platform from Capacitor.
  const platform = Capacitor.getPlatform() === "ios" ? "ios" : "android";
  const regRes = await fetchWithAuth("/v1/team-messaging/push/devices", {
    method: "POST",
    body: JSON.stringify({
      subscription: token, // native = raw token string (backend differentiates by platform)
      platform,
      appVersion: `${platform}/Capacitor`,
    }),
  });
  if (!regRes.ok) {
    const err = await regRes.json().catch(() => ({}));
    throw new Error(err.error || "Failed to register device");
  }
  const { device } = await regRes.json();
  return device;
}

/** Remove the local push subscription AND the server-side device row. */
export async function unsubscribeFromPush(deviceId) {
  try {
    if (deviceId) {
      await fetchWithAuth(`/v1/team-messaging/push/devices/${deviceId}`, {
        method: "DELETE",
      });
    }
  } catch (err) {
    console.warn("[push] server unregister failed:", err);
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch (err) {
    console.warn("[push] local unsubscribe failed:", err);
  }
}

/** Trigger a test push to the current user — useful from a debug button. */
export async function sendTestPush() {
  const res = await fetchWithAuth("/v1/team-messaging/push/test", { method: "POST" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Test push failed");
  }
  return res.json();
}
